import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useButtonLinks } from '../hooks/useButtonLinks.js';
import { useTranslation } from '../hooks/useTranslation.js';

const Forex = () => {
  const { buttonLinks, handleButtonClick } = useButtonLinks();
  const [activeTab, setActiveTab] = useState('major');
  const { t } = useTranslation();

  // Sample forex instruments data
  const forexInstruments = [
    { symbol: 'EUR/USD', description: 'Euro vs US Dollar', contractSize: '100,000', marginRate: '3.33%', spread: '0.8' },
    { symbol: 'GBP/USD', description: 'British Pound vs US Dollar', contractSize: '100,000', marginRate: '5%', spread: '1.2' },
    { symbol: 'USD/JPY', description: 'US Dollar vs Japanese Yen', contractSize: '100,000', marginRate: '3.33%', spread: '0.9' },
    { symbol: 'AUD/USD', description: 'Australian Dollar vs US Dollar', contractSize: '100,000', marginRate: '5%', spread: '1.4' },
    { symbol: 'USD/CAD', description: 'US Dollar vs Canadian Dollar', contractSize: '100,000', marginRate: '3.33%', spread: '1.6' }
  ];

  return (
    <div className="forex-page">
      {/* Hero Section */}
      <section className="forex-hero">
        <div className="container">
          <div className="forex-hero-content">
            <h1 className="forex-title">{t('markets.forex.hero.title')}</h1>
            <p className="forex-subtitle">
              {t('markets.forex.hero.subtitle')}
            </p>
            <div className="hero-question">
              <h2>{t('markets.forex.hero.description')}</h2>
            </div>
            <div className="forex-features">
              <div className="feature-highlight">
                <i className="fas fa-chart-line"></i>
                <span>{t('markets.forex.whyTrade.benefits.0.description')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-tools"></i>
                <span>{t('markets.forex.whyTrade.benefits.3.description')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Forex Instruments Section */}
      <section className="forex-instruments">
        <div className="container">
          <div className="instruments-header">
            <h2 className="section-title">{t('markets.forex.instruments.title')}</h2>
            <button className="btn btn-secondary">{t('markets.forex.instruments.subtitle')}</button>
          </div>
          
          <div className="instruments-table-wrapper">
            <table className="instruments-table">
              <thead>
                <tr>
                  <th>{t('markets.forex.instruments.headers.symbol')}</th>
                  <th>{t('markets.forex.instruments.headers.description')}</th>
                  <th>Contract Size</th>
                  <th>Margin Rate</th>
                  <th>{t('markets.forex.instruments.headers.minSpread')}</th>
                </tr>
              </thead>
              <tbody>
                {forexInstruments.map((instrument, index) => (
                  <tr key={index}>
                    <td className="symbol">{instrument.symbol}</td>
                    <td className="description">{instrument.description}</td>
                    <td>{instrument.contractSize}</td>
                    <td>{instrument.marginRate}</td>
                    <td className="spread">{instrument.spread}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <p className="instruments-note">
            {t('markets.forex.instruments.note')}
          </p>
        </div>
      </section>

      {/* Why Trade Forex Section */}
      <section className="why-trade-forex">
        <div className="container">
          <h2 className="section-title">{t('markets.forex.whyTrade.title')}</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <h3>{t('markets.forex.whyTrade.benefits.0.title')}</h3>
              <p>{t('markets.forex.whyTrade.benefits.0.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-bolt"></i>
              </div>
              <h3>{t('markets.forex.whyTrade.benefits.1.title')}</h3>
              <p>{t('markets.forex.whyTrade.benefits.1.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3>{t('markets.forex.whyTrade.benefits.2.title')}</h3>
              <p>{t('markets.forex.whyTrade.benefits.2.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-mobile-alt"></i>
              </div>
              <h3>{t('markets.forex.whyTrade.benefits.3.title')}</h3>
              <p>{t('markets.forex.whyTrade.benefits.3.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-globe"></i>
              </div>
              <h3>{t('markets.forex.whyTrade.benefits.4.title')}</h3>
              <p>{t('markets.forex.whyTrade.benefits.4.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-chart-bar"></i>
              </div>
              <h3>{t('markets.forex.whyTrade.benefits.5.title')}</h3>
              <p>{t('markets.forex.whyTrade.benefits.5.description')}</p>
            </div>
          </div>
          
          <div className="forex-cta">
            <h3>{t('markets.forex.accountComparison.subtitle')}</h3>
            <button 
              className="btn btn-primary"
              onClick={(e) => handleButtonClick('openAccount', e)}
            >
              {t('nav.openAccount')}
            </button>
          </div>
        </div>
      </section>

      {/* Account Comparison Section */}
      <section className="account-comparison">
        <div className="container">
          <h2 className="section-title">{t('markets.forex.accountComparison.title')}</h2>
          <div className="accounts-cards">
            
            {/* Pro Account */}
            <div className="account-card">
              <div className="account-header">
                <h3 className="account-name">{t('markets.forex.accountComparison.accounts.pro')}</h3>
                <div className="account-price">
                  <span className="deposit-amount">$20</span>
                  <span className="deposit-label">{t('markets.forex.accountComparison.features.startingDeposit')}</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.spreadsFrom')}</span>
                  <span className="feature-value">1 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.commission')}</span>
                  <span className="feature-value">$0</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.leverage')}</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.platform')}</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.islamicAccounts')}</span>
                  <span className="feature-value">{t('markets.forex.accountComparison.features.available')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.orderExecution')}</span>
                  <span className="feature-value">{t('markets.forex.accountComparison.features.market')}</span>
                </div>
              </div>
              <Link to="/accounts/pro" className="btn btn-primary account-btn">{t('markets.forex.accountComparison.features.findOutMore')}</Link>
            </div>

            {/* Prime Account */}
            <div className="account-card featured">
              <div className="popular-badge">Most Popular</div>
              <div className="account-header">
                <h3 className="account-name">{t('markets.forex.accountComparison.accounts.prime')}</h3>
                <div className="account-price">
                  <span className="deposit-amount">$250</span>
                  <span className="deposit-label">{t('markets.forex.accountComparison.features.startingDeposit')}</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.spreadsFrom')}</span>
                  <span className="feature-value">0 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.commission')}</span>
                  <span className="feature-value">$6</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.leverage')}</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.platform')}</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.islamicAccounts')}</span>
                  <span className="feature-value">{t('markets.forex.accountComparison.features.available')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.orderExecution')}</span>
                  <span className="feature-value">{t('markets.forex.accountComparison.features.market')}</span>
                </div>
              </div>
              <Link to="/accounts/prime" className="btn btn-primary account-btn">{t('markets.forex.accountComparison.features.findOutMore')}</Link>
            </div>

            {/* Premium Account */}
            <div className="account-card">
              <div className="account-header">
                <h3 className="account-name">{t('markets.forex.accountComparison.accounts.premium')}</h3>
                <div className="account-price">
                  <span className="deposit-amount">$3000</span>
                  <span className="deposit-label">{t('markets.forex.accountComparison.features.startingDeposit')}</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.spreadsFrom')}</span>
                  <span className="feature-value">0 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.commission')}</span>
                  <span className="feature-value">$3</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.leverage')}</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.platform')}</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.islamicAccounts')}</span>
                  <span className="feature-value">{t('markets.forex.accountComparison.features.available')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.forex.accountComparison.features.orderExecution')}</span>
                  <span className="feature-value">{t('markets.forex.accountComparison.features.market')}</span>
                </div>
              </div>
              <Link to="/accounts/premium" className="btn btn-primary account-btn">{t('markets.forex.accountComparison.features.findOutMore')}</Link>
            </div>

          </div>
          
          <div className="demo-account">
            <h3>Not ready yet?</h3>
            <p>Try the demo account first to see what's in store for you!</p>
            <button 
              className="btn btn-secondary"
              onClick={(e) => handleButtonClick('tryDemo', e)}
            >
              Create Demo Account
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Forex;